import React, { useState } from "react";
import { Trash2, X, Loader2, AlertTriangle } from "lucide-react";

/**
 * Confirmation modal for project deletion.
 * Brutalist styling matches the rest of the app — orange warning chrome.
 *
 * Props:
 *   design       Project being deleted (used for the name in the dialog).
 *   onClose      Dismiss without deleting.
 *   onConfirm    Async — caller is responsible for the delete API call;
 *                this component only handles the loading + close states.
 */
export default function DeleteProjectDialog({ design, onClose, onConfirm }) {
  const [loading, setLoading] = useState(false);
  if (!design) return null;

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await onConfirm();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-zinc-950/85 backdrop-blur-sm p-4"
      data-testid="delete-project-dialog"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-md border border-orange-600/60 bg-zinc-950 shadow-[0_20px_60px_rgba(0,0,0,0.7)]">
        <header className="flex items-start justify-between gap-3 px-5 py-4 border-b border-zinc-800">
          <div className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-orange-400">
            <AlertTriangle className="w-3.5 h-3.5" />
            // Delete Project
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="delete-project-close"
            className="text-zinc-400 hover:text-zinc-50 transition-colors"
            aria-label="Close dialog"
          >
            <X className="w-4 h-4" />
          </button>
        </header>
        <div className="px-5 py-5 space-y-3">
          <h2 className="font-display text-2xl text-zinc-50 tracking-tight leading-snug">
            Delete <span className="text-orange-300">{design.name}</span>?
          </h2>
          <p className="font-body text-sm text-zinc-400 leading-relaxed">
            This permanently removes the blueprint, chat history, and any
            generated PDFs. This action cannot be undone.
          </p>
          {design.status === "sent_for_review" && (
            <div className="border border-brass-700/40 bg-brass-950/20 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-brass-300">
              // This design has been sent for review
            </div>
          )}
        </div>
        <footer className="flex items-center justify-end gap-2 px-5 py-4 border-t border-zinc-800 bg-zinc-950/60">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            data-testid="delete-project-cancel"
            className="font-mono text-[10px] uppercase tracking-widest px-4 py-2 border border-zinc-700 text-zinc-300 hover:text-zinc-50 hover:border-zinc-500 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={loading}
            data-testid="delete-project-confirm"
            className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest px-4 py-2 bg-orange-600 hover:bg-orange-500 text-white transition-colors disabled:opacity-60"
          >
            {loading ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Trash2 className="w-3.5 h-3.5" />
            )}
            {loading ? "Deleting..." : "Delete Project"}
          </button>
        </footer>
      </div>
    </div>
  );
}
